import type { AccountAiCreditsResponse } from 'definitions/editor-ai-ui';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'hooks/useAuth';
import { useClient } from 'hooks/useClient';
import { aiCreditMeterState } from './ai-credit-meter';

/** The shared prefix: invalidating it refreshes every account's meter, as after a results AI turn. */
export const AI_CREDIT_METER_KEY = ['AI_CREDIT_METER'] as const;

type UseAiCreditMeterParams = {
  /** An agency's client; the caller's own account when omitted. */
  accountId?: number | null;
};

/**
 * The AI credits left for one account, for the header. Backed by `GET /api/billing/ai-credits`,
 * which reads the meter the editor and the results chat read, so all three agree: the credit
 * ledger for an account on credits, the AI's monthly cap for any other.
 */
export const useAiCreditMeter = ({ accountId = null }: UseAiCreditMeterParams = {}) => {
  const apiClient = useClient();
  const { user } = useAuth();

  const { data, isError } = useQuery<AccountAiCreditsResponse>({
    queryKey: [...AI_CREDIT_METER_KEY, accountId ?? 'self'],
    enabled: !!user,
    staleTime: 60_000,
    queryFn: () =>
      apiClient.get(accountId ? `/billing/ai-credits?accountCompanyId=${accountId}` : '/billing/ai-credits'),
  });

  // Never hidden: loading, a failed read and a caller with no account each have a pill of their own.
  return aiCreditMeterState({ response: data, isError });
};
